import clsx from "clsx";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, Link as RouterLink, useHistory } from "react-router-dom";

import {
  Grid,
  Button,
  Typography,
  Divider,
  IconButton,
  List,
  ListItem,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import EditIcon from "@material-ui/icons/Edit";

import { FleetContext, FleetSettingsContext } from "../../context";
import {
  SectionGridLayout,
  SectionGridContainer,
  PermissionedDisplay,
} from "../../components/Layout";
import FleetSettingsRouter from "../../routers/FleetSettingsRouter";
import Permissions from "../../permissions";
import { FLEET_SETTING_PAGES } from "../../constants";
import { StringUtils } from "../../util";

const useStyles = makeStyles((theme) => ({
  navSelected: { color: theme.palette.primary.button },
  vertDivider: { borderRight: `1px solid ${theme.palette.primary.divider}` },
  horizontalDivider: { borderTop: `1px solid ${theme.palette.primary.divider}` },
  buttonBackground: { background: theme.palette.primary.red },
  cancelButton: { marginRight: theme.spacing(2) },
  noPad: { paddingLeft: 0, paddingRight: 0 },
  fullWidth: { width: "100%" },
  backButton: { marginRight: theme.spacing(1) },
}));

const FleetSettings = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const { activeFleet, updateActiveFleet, setActiveFleet } = useContext(FleetContext);
  const settingsContext = useContext(FleetSettingsContext);
  const { fleetId } = useParams();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState(FLEET_SETTING_PAGES[0]);
  const [loading, setLoading] = useState(false);
  const [editPerm, setEditPerm] = useState(undefined);
  const history = useHistory();

  const saveChanges = async () => {
    setLoading(true);
    if (currentTab === "general") {
      const data = await settingsContext.schemas[currentTab].cast(settingsContext[currentTab]);
      await updateActiveFleet(data);
    } else if (currentTab === "billing") {
      const data = await settingsContext.schemas[currentTab].cast(settingsContext[currentTab]);
      const ratePlan = await settingsContext.updateActiveRatePlan(activeFleet.rate_plan_id, data);
      if (ratePlan) {
        await updateActiveFleet({
          uuid4: fleetId,
          rate_plan_id: StringUtils.uuidFromUrl(ratePlan.url),
          external_rate_plan: ratePlan.url,
        });
      }
    }
    setLoading(false);
    history.push(history.location.pathname.slice(0, history.location.pathname.lastIndexOf("/")));
  };

  useEffect(() => {
    setActiveFleet(fleetId);
    const newCurrentTab = FLEET_SETTING_PAGES.find((tab) => location.pathname.includes(tab));
    setCurrentTab(newCurrentTab);
    if (newCurrentTab === "general") {
      setEditPerm(Permissions.writeFleets);
    } else if (newCurrentTab === "billing") {
      setEditPerm(Permissions.writeRatePlans);
    } else if (newCurrentTab === "passes") {
      setEditPerm([Permissions.writePassOffers, Permissions.feature.passes]);
    } else if (newCurrentTab === "messaging") {
      setEditPerm([Permissions.writeToastMessages, Permissions.feature.messaging]);
    }
  }, [location.pathname, fleetId]);

  const godButton = (page) => {
    if (page.includes("edit") && !page.includes("messaging")) {
      return (
        <>
          <Button
            className={classes.cancelButton}
            component={RouterLink}
            to={`/fleets/${fleetId}/settings/${currentTab}`}
          >
            {t("Fleet.Settings.Nav.Cancel")}
          </Button>
          <Button
            data-testid="fs-god-save-button"
            disabled={!!settingsContext.invalids[currentTab].length}
            onClick={saveChanges}
            variant="contained"
            color="secondary"
            disableElevation
          >
            {t("Fleet.Settings.Nav.Save")}
          </Button>
        </>
      );
    }
    if (page.includes("passes") || page.includes("messaging")) {
      return (
        <PermissionedDisplay permission={editPerm}>
          <IconButton
            data-testid="fs-god-add-button"
            className={classes.buttonBackground}
            onClick={() =>
              history.push({
                pathname: `${location.pathname}/new`,
                state: { settingsBackground: location },
              })
            }
          >
            <AddRoundedIcon />
          </IconButton>
        </PermissionedDisplay>
      );
    }
    return (
      <PermissionedDisplay permission={editPerm}>
        <IconButton
          data-testid="fs-god-edit-button"
          className={classes.buttonBackground}
          component={RouterLink}
          to={`/fleets/${fleetId}/settings/${currentTab}/edit`}
        >
          <EditIcon />
        </IconButton>
      </PermissionedDisplay>
    );
  };

  return (
    <SectionGridLayout>
      <SectionGridContainer spaceContents>
        <Grid item container xs={12} justify="space-between">
          <Grid item container alignItems="center" xs={12} md={6}>
            <IconButton
              className={classes.backButton}
              component={RouterLink}
              to={`/fleets/${fleetId}`}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {`${activeFleet?.name} ${t("Fleet.Settings.Nav.FleetSettings")}`}
            </Typography>
          </Grid>
          <Grid item container justify="flex-end" xs={12} md={6}>
            {godButton(location.pathname)}
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.noPad}>
          {loading && <LinearProgress color="secondary" />}
          <Divider />
        </Grid>
        <Grid item container xs={12} md={2} className={classes.vertDivider}>
          <List className={classes.fullWidth}>
            <PermissionedDisplay permission={[Permissions.readFleets]}>
              <ListItem
                key="general"
                button
                component={RouterLink}
                to={`/fleets/${fleetId}/settings/general`}
                className={clsx(currentTab === "general" && classes.navSelected)}
              >
                {t("Fleet.Settings.Nav.general")}
              </ListItem>
            </PermissionedDisplay>
            <PermissionedDisplay permission={[Permissions.readRatePlans]}>
              <ListItem
                key="billing"
                button
                component={RouterLink}
                to={`/fleets/${fleetId}/settings/billing`}
                className={clsx(currentTab === "billing" && classes.navSelected)}
              >
                {t("Fleet.Settings.Nav.billing")}
              </ListItem>
            </PermissionedDisplay>
            <PermissionedDisplay
              permission={[
                Permissions.feature.passes,
                Permissions.readPassOffers,
                Permissions.readPassOfferTypes,
              ]}
            >
              <ListItem
                key="passes"
                button
                component={RouterLink}
                to={`/fleets/${fleetId}/settings/passes`}
                className={clsx(currentTab === "passes" && classes.navSelected)}
              >
                {t("Fleet.Settings.Nav.passes")}
              </ListItem>
            </PermissionedDisplay>
            <PermissionedDisplay
              permission={[Permissions.readToastMessages, Permissions.feature.messaging]}
            >
              <ListItem
                key="messaging"
                button
                component={RouterLink}
                to={`/fleets/${fleetId}/settings/messaging`}
                className={clsx(currentTab === "messaging" && classes.navSelected)}
              >
                {t("Fleet.Settings.Nav.messaging")}
              </ListItem>
            </PermissionedDisplay>
            <PermissionedDisplay
              permission={[Permissions.writeGeofences, Permissions.feature.geofenceCreation]}
            >
              <ListItem
                key="geofences"
                button
                component={RouterLink}
                to={`/fleets/${fleetId}/geofences`}
              >
                {t("Fleet.Settings.Nav.Geofences")}
              </ListItem>
            </PermissionedDisplay>
            <PermissionedDisplay permission={Permissions.feature.sidewalks}>
              <ListItem
                key="sidewalks"
                button
                component={RouterLink}
                to={`/fleets/${fleetId}/sidewalks`}
              >
                {t("Fleet.Settings.Nav.Sidewalks")}
              </ListItem>
            </PermissionedDisplay>
          </List>
        </Grid>
        <Grid item xs={12} md={10}>
          <FleetSettingsRouter />
        </Grid>
      </SectionGridContainer>
    </SectionGridLayout>
  );
};

export default FleetSettings;
